import { useState, useEffect } from "react";
import styles from "./Billboards.module.css";
import Billboard from "../../components/Billboard";

import ButtonDivider from "../../components/ButtonDivider";
import MyBillboard from "../../components/MyBillboard";

const COST_PER_MONTH = 200;

function AddBillboardButton({ onClick }) {
  return (
    <div className={styles.addBillboardRow}>
      <button className={styles.addBillboardButton} onClick={onClick}>
        + Add New Billboard
      </button>
    </div>
  );
}

const Billboards = ({ user, userSteedieTokens, onBuySuccess }) => {
  const [billboards, setBillboards] = useState([
    { id: 1, image: null, months: 1 },
  ]);
  const [tokenCost, setTokenCost] = useState(200);
  const [myBillboards, setMyBillboards] = useState([]);
  const [uploadMessages, setUploadMessages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isConfirmDisabled =
    isSubmitting ||
    !billboards.some((b) => b.image) ||
    userSteedieTokens < tokenCost;

  useEffect(() => {
    const totalCost = billboards.reduce(
      (sum, b) => (b.image ? sum + b.months * COST_PER_MONTH : sum),
      0
    );
    setTokenCost(totalCost);
  }, [billboards]);

  const CACHE_MY_BILLBOARDS_KEY = "myBillboardsCache";
  const CACHE_MY_BILLBOARDS_EXPIRATION = 30 * 1000;

  useEffect(() => {
    if (!user) return;

    const fetchMyBillboards = async () => {
      const cached = localStorage.getItem(CACHE_MY_BILLBOARDS_KEY);
      if (cached) {
        const { timestamp, data } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_MY_BILLBOARDS_EXPIRATION) {
          setMyBillboards(data);
          return;
        }
      }

      const res = await fetch(
        "https://eynulcvqk1.execute-api.eu-central-1.amazonaws.com/get-my-billboards",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.jwt}`,
          },
        }
      );

      if (res.ok) {
        const data = await res.json();
        setMyBillboards(data);
        localStorage.setItem(
          CACHE_MY_BILLBOARDS_KEY,
          JSON.stringify({ timestamp: Date.now(), data })
        );
      }
    };

    fetchMyBillboards();
  }, [user, CACHE_MY_BILLBOARDS_KEY, CACHE_MY_BILLBOARDS_EXPIRATION]);

  const handleImageUpload = (id, image) => {
    setBillboards((prev) =>
      prev.map((b) => (b.id === id ? { ...b, image } : b))
    );
  };

  const handleDurationChange = (id, months) => {
    setBillboards((prev) =>
      prev.map((b) => (b.id === id ? { ...b, months } : b))
    );
  };

  const addBillboard = () => {
    const newId = Math.max(0, ...billboards.map((b) => parseInt(b.id))) + 1;
    const uniqueId = `${newId}-${Date.now()}`;
    const newBillboard = { id: uniqueId, image: null, months: 1 };

    setBillboards((prev) => [...prev, newBillboard]);
    console.log([...billboards, newBillboard]);
  };

  const handleDelete = (id) => {
    setBillboards((prev) => prev.filter((b) => b.id !== id));
  };

  const handleConfirmUpload = async () => {
    if (isConfirmDisabled) {
      return;
    }
    setIsSubmitting(true);
    const user = JSON.parse(localStorage.getItem("steedie-user"));
    if (!user) {
      setUploadMessages([{ type: "error", text: "Not signed in." }]);
      setIsSubmitting(false);
      return;
    }

    const remaining = [];
    const messages = [];

    for (const [index, b] of billboards.entries()) {
      if (!b.image) {
        remaining.push(b);
        continue;
      }

      try {
        const res = await fetch(
          "https://eynulcvqk1.execute-api.eu-central-1.amazonaws.com/upload-ads",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.jwt}`,
            },
            body: JSON.stringify({
              base64Image: b.image,
              months: b.months,
            }),
          }
        );

        if (!res.ok) {
          const errorBody = await res.text();
          messages.push({
            type: "error",
            text: `❌ Billboard ${index + 1}: ${res.status} - ${errorBody}`,
          });
          remaining.push(b);
        } else {
          const data = await res.json();
          messages.push({
            type: "success",
            text: `✅ Billboard ${index + 1} uploaded successfully.`,
          });
          setMyBillboards((prev) => {
            const updated = [...prev, data];
            localStorage.setItem(
              CACHE_MY_BILLBOARDS_KEY,
              JSON.stringify({ timestamp: Date.now(), data: updated })
            );
            return updated;
          });
          onBuySuccess(data.spent);
        }
      } catch (err) {
        messages.push({
          type: "error",
          text: `❌ Billboard ${index + 1}: Upload failed - ${err.message}`,
        });
        remaining.push(b);
      }
    }

    setBillboards(remaining);
    setUploadMessages(messages);
    setIsSubmitting(false);
  };

  const handleSteamLogin = () => {
    const redirectUri = window.location.href;
    window.location.href = `https://eynulcvqk1.execute-api.eu-central-1.amazonaws.com/auth/steam?redirectUri=${encodeURIComponent(
      redirectUri
    )}`;
  };

  return (
    <div className={styles.billboards}>
      <div className={styles.left}>
        <ButtonDivider text={"NEW BILLBOARDS"} />
        {billboards.map((b) => (
          <Billboard
            key={b.id}
            id={b.id}
            uploadedImage={b.image}
            duration={b.months}
            onImageUpload={(img) => handleImageUpload(b.id, img)}
            onDurationChange={(months) => handleDurationChange(b.id, months)}
            onDelete={() => handleDelete(b.id)}
            costPerMonth={COST_PER_MONTH}
          />
        ))}
        <AddBillboardButton
          onClick={
            !user && billboards.length > 0 ? handleSteamLogin : addBillboard
          }
        />

        {myBillboards?.length > 0 && (
          <>
            <ButtonDivider text={"MY BILLBOARDS"} />
            {myBillboards
              .slice()
              .sort((a, b) => {
                const order = {
                  REJECTED: 0,
                  "PENDING APPROVAL": 1,
                  ACTIVE: 2,
                  EXPIRED: 3,
                };
                return order[a.status] - order[b.status];
              })
              .map((b) => (
                <MyBillboard key={b.id} b={b} />
              ))}
          </>
        )}
      </div>
      <div className={styles.right}>
        <div className={styles.rightContent}>
          <div className={styles.receipt}>
            <h3 className={styles.receiptTitle}>
              {user ? "Summary" : "Please sign in before continuing"}
            </h3>
            {billboards.map((b, i) =>
              b.image ? (
                <div key={b.id} className={styles.receiptItem}>
                  <div>Billboard {i + 1}</div>
                  <div>
                    {b.months} month{b.months > 1 ? "s" : ""}
                  </div>
                  <div>🪙 {b.months * COST_PER_MONTH}</div>
                </div>
              ) : null
            )}
          </div>

          {uploadMessages.length > 0 && (
            <div className={styles.uploadMessages}>
              {uploadMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`${styles.uploadMessage} ${
                    msg.type === "error" ? styles.error : styles.success
                  }`}
                >
                  {msg.text}
                </div>
              ))}
            </div>
          )}

          <button
            className={`${styles.confirmButton} ${
              isSubmitting
                ? styles.confirmButtonLoading
                : isConfirmDisabled
                ? styles.confirmButtonDisabled
                : ""
            }`}
            onClick={user ? handleConfirmUpload : handleSteamLogin}
            disabled={user && isConfirmDisabled}
          >
            <p>{isSubmitting ? "UPLOADING..." : "CONFIRM"}</p>
            <div className={styles.steedieTokens}>
              <div className={styles.tokensText}>🪙{tokenCost}</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Billboards;
